import { isGroup } from "./keys";
import { writePoints } from "./points";

// The two readings of the one box, and what a page opened on that mode starts with. A blank box would put six cards
// over nothing, so the sample is a domain that reads as `apple.com` and is not one: the `а` is Cyrillic, which is
// the finding this page exists for, and the `é` and the cup are there for the normal forms and the surrogate pair.
export const MODES = {
  text: {
    label: "Text",
    title: "Unicode Inspector",
    field: "Text",
    hint: "Anything at all — a name, a filename, a line out of a diff",
  },
  points: {
    label: "Code points",
    title: "Unicode Code Points",
    field: "Code points",
    hint: "Hex, however they were written: U+0041, 0041, 0x41, \\u{41} or &#x41;",
  },
};

export type Mode = keyof typeof MODES;

export const MODE_OPTIONS = Object.entries(MODES).map(([value, { label }]) => ({ value, label }));

// Spelled in escapes rather than in the letters themselves, since the whole point of the `а` is that no file can
// show it is Cyrillic — which is also the one thing this page is for.
export const SAMPLE = "Caf\u00E9 \u2615 \u0430pple.com";

// Every character of a long paste is counted, weighed and searched for a finding; only this many are drawn, a table
// of ten thousand rows being a page that takes a second to lay out and stutters as it is scrolled.
export const MAX_ROWS = 512;

export function pickMode(value: unknown): Mode {
  return value === "points" ? "points" : "text";
}

// A link with no text in it opens on the sample, /json's reasoning: absent and empty differ, so both are carried.
export function pickValue(value: unknown, mode: Mode): string {
  if (typeof value === "string") return value;
  return mode === "points" ? writePoints(SAMPLE) : SAMPLE;
}

// Which group of characters the keyboard is showing — a block of the standard or a group of emoji — and whether it
// is showing at all: the field is absent when the keyboard is closed, so its presence is the one and the value the
// other.
export function pickGroup(value: unknown): string | null {
  return isGroup(value) ? value : null;
}

// Which character the cards below the table are about. A link made before the text was edited can name one that is
// no longer there, so it is clamped where it is read rather than trusted.
export function pickAt(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : 0;
}
